import { useQuery } from "@tanstack/react-query";
import { Mail, Users, Globe, Trophy, MapPin, Handshake, Building2, Briefcase, Map, Monitor, TreePine, GraduationCap, Footprints } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchHeroStats } from "@/api/geography";
import { PageHeader } from "@/components/PageHeader";
import { usePageSeo } from "@/hooks/usePageSeo";
import { statsKeys } from "@/api/queryKeys";

const CONTACT_EMAIL = "quizrun.org@gmail.com";
const CONTACT_TELEGRAM = "https://t.me/jmeshcheryakova";

const Partnership = () => {
  usePageSeo(
    "Partner with Quizrun — Sponsor, Team Building & Host an Event",
    "Sponsor a Quizrun event, book a private team-building run for your company, or bring Quizrun to your city. 400+ participants across 50+ cities in 10+ countries.",
  );
  const { data: heroStats } = useQuery({
    queryKey: statsKeys.heroPartnership,
    queryFn: fetchHeroStats,
    staleTime: 10 * 60 * 1000,
  });

  return (
    <div>
      <PageHeader
        eyebrow="Partnership"
        title="Grow with Quizrun"
        intro="Sponsor an event, bring Quizrun to your city, or book a private event for your team. Three ways to work with us."
      >
        {/* Social proof stats */}
        {heroStats && (
          <div className="flex flex-wrap gap-6 mt-8 text-sm">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Users className="h-4 w-4 text-primary" />
              <span className="font-bold text-foreground">{heroStats.uniqueFinishers}+</span> participants
            </span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <span className="font-bold text-foreground">{heroStats.totalCities}</span> cities
            </span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Globe className="h-4 w-4 text-primary" />
              <span className="font-bold text-foreground">{heroStats.totalCountries}</span> countries
            </span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Trophy className="h-4 w-4 text-primary" />
              <span className="font-bold text-foreground">{heroStats.finishedEvents}</span> events held
            </span>
          </div>
        )}
      </PageHeader>

      {/* Three tracks */}
      <main className="container mx-auto px-4 md:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

          {/* Sponsor track */}
          <div className="rounded-xl border bg-card p-6 md:p-8 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <Handshake className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold text-foreground">Become a Sponsor</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Get your brand in front of an active, curious audience at Quizrun events worldwide.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Trophy className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-foreground">Event Sponsorship</div>
                  <div className="text-xs text-muted-foreground">Sponsor a single event — logo on materials, social media mentions, on-site presence</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-foreground">Title Sponsorship</div>
                  <div className="text-xs text-muted-foreground">Become the main sponsor across multiple events and cities</div>
                </div>
              </div>
            </div>
          </div>

          {/* Corporate & team building track */}
          <div className="rounded-xl border bg-card p-6 md:p-8 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold text-foreground">Corporate &amp; Team Building</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              A private Quizrun for your team — running meets lateral-thinking puzzles. A sharper alternative to another escape room or pub quiz.
            </p>

            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span> Private event for your group (10–100+)
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span> Custom puzzles + a timed running route in your local park
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span> Live scoring, leaderboard, and prizes
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span> We run it end-to-end — you just show up
              </div>
            </div>
          </div>

          {/* Organiser track */}
          <div className="rounded-xl border bg-card p-6 md:p-8 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <Map className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold text-foreground">Organise in Your City</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              We provide the format, you bring the park and the people.
            </p>

            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span> Quiz questions + scoring system
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span> Help setting up your local route
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span> Event page on quizrun.org
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span> Promotion across Quizrun channels
              </div>
            </div>
          </div>
        </div>

        {/* Add to your event */}
        <div className="mt-12 rounded-xl border bg-card p-6 md:p-8">
          <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-6">Add Quizrun to your event</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-lg bg-muted/50 p-4">
              <Monitor className="h-5 w-5 text-primary mb-2" />
              <div className="text-sm font-medium text-foreground">Professional conferences</div>
              <div className="text-xs text-muted-foreground">Active break between talks — get people moving and competing</div>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <TreePine className="h-5 w-5 text-primary mb-2" />
              <div className="text-sm font-medium text-foreground">Community festivals</div>
              <div className="text-xs text-muted-foreground">Interactive outdoor activity for park days, charity runs, or local fairs</div>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <Footprints className="h-5 w-5 text-primary mb-2" />
              <div className="text-sm font-medium text-foreground">Running events</div>
              <div className="text-xs text-muted-foreground">Add a quiz layer to an existing 5K — same route, extra challenge</div>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <GraduationCap className="h-5 w-5 text-primary mb-2" />
              <div className="text-sm font-medium text-foreground">Universities</div>
              <div className="text-xs text-muted-foreground">Freshers week, sports day, or faculty mixer — active icebreaker</div>
            </div>
          </div>
        </div>

        {/* Single CTA */}
        <div className="mt-12 rounded-xl border bg-card p-6 md:p-8 text-center">
          <h2 className="text-lg font-bold text-foreground mb-2">Interested?</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Drop us a message — we'll get back within 48 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Quizrun Partnership`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              <Mail className="h-4 w-4" />
              {CONTACT_EMAIL}
            </a>
            <a
              href={CONTACT_TELEGRAM}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              💬 Telegram
            </a>
          </div>
          {/* Individual, not a business? Point them to Support instead of the
              partnership tracks — also an internal link that helps /support rank. */}
          <p className="mt-6 text-sm text-muted-foreground">
            Not a business? You can still{" "}
            <Link to="/support" className="font-medium text-primary hover:underline">
              support Quizrun as an individual →
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default Partnership;
